import { intersect } from '@flatten-js/boolean-op';
import { Point, Polygon, Segment, Vector, ORIENTATION } from '@flatten-js/core';
import { addBreakerboardAreaSide, simplifyPolygon } from '../geometrical';
import { getMainPolygonFaceIndex, isInnerBorder } from '../geometrical/calc-auto-breakerboard';
import { splitPolygon } from '../geometrical/poly-split';
import {
  getFacePoints,
  getPolygonPoints,
  orderPoints,
  isEqualInTolerance,
  containsPoint,
  mergeTwoLengthCounts,
  getLengthCountsForArea,
} from '../helpers';
import { BreakerBoard } from './breaker-boards';
import { AreaBorderLabelState } from './border-labels';
import { cmToMm } from '../../utils/number-converter';

/**
 * Class representing of the state of single area.
 * It has all the attributes received from backend stored in the rawArea property.
 * The backend representation of the area can be requested by calling the getter "backendObjects".
 * Then the rawArea.data object is cloned and modified by the current state of the area.
 */
export class AreaState {
  constructor(options) {
    ({
      points: this.points = [],
      breakerBoards: this.breakerBoards = [],
      rawArea: this.rawArea,
      connectionStorage: this.connectionStorage,
      id: this.id,
      cutoutPolygons: this.cutoutPolygons = [],
      groupId: this.groupId,
      availableBoards: this.availableBoards = [],
      edgeLabelingOptions: this.edgeLabelingOptions = [],
      edgeLabelingMapping: this.edgeLabelingMapping = [],
      debug: this.debug = false,
    } = options);
    this.parallelLayoutOptions = this._validateDisplayedParallelLayoutOptions(options.displayedParallelLayoutOptions);
    if (options.vertices) {
      for (const vertex of options.vertices) {
        this.points.push(new Point(vertex.x, vertex.y));
      }
    }
    if (!this.points.length) {
      throw Error('Cannot create area without points');
    }

    if (!this.connectionStorage) {
      throw Error('No connection storage supplied');
    }

    if (this.rawArea && this.rawArea.data.descriptor.original_points) {
      this.originalPoints = [];
      for (const vertex of this.rawArea.data.descriptor.original_points) {
        this.originalPoints.push(new Point(vertex.x, vertex.y));
      }
    } else {
      this.originalPoints = dojo.clone(this.points);
    }

    if (this.rawArea && this.rawArea.tessellation.cutoutPolygons) {
      this.rawArea.tessellation.cutoutPolygons.forEach(poly => {
        this.cutoutPolygons.push(poly.points.map(vertex => new Point(vertex.x, vertex.y)));
      });
    }

    // If this.rawArea is not set, the following assignment produces an "empty"
    // border label state.
    this.borderLabelState = new AreaBorderLabelState(this.rawArea, this.edgeLabelingOptions, this.edgeLabelingMapping);
  }

  _validateDisplayedParallelLayoutOptions(displayedParallelLayoutOptions) {
    const defaultOptions = {
      maxBoardEndLinesDelta: 2,
      weightedSolutionsBias: 0.05,
      maxNumProposals: 6,
    };
    return Object.assign(defaultOptions, displayedParallelLayoutOptions);
  }

  _createAreaStateFromPolygon(polygon, newId, availableBoards) {
    const options = {
      points: getPolygonPoints(simplifyPolygon(polygon)),
      id: newId,
      connectionStorage: this.connectionStorage,
      groupId: this.groupId,
      availableBoards: availableBoards,
      displayedParallelLayoutOptions: this.parallelLayoutOptions,
      edgeLabelingOptions: this.edgeLabelingOptions,
      edgeLabelingMapping: this.edgeLabelingMapping,
    };

    if (polygon.faces.size > 1) {
      const cutouts = [];
      const faces = [...polygon.faces];
      for (let i = 1; i < faces.length; i++) {
        const face = faces[i];
        cutouts.push(getFacePoints(face));
      }
      options.cutoutPolygons = cutouts;
    }
    const area = new AreaState(options);
    area.backendObjectFromParent = this.backendObjectForChildren;
    area.updateRawAreaData('active_edge', this._findNewAreaBaseBorderIndex(area));
    return area;
  }

  /**
   * Splits the current area state into two parts by specified segment.
   * Unique IDs are set to the newly created states.
   * @param  {Flatten.Segment} splitSegment
   * @param  {IdStorage} idStorage
   * @returns {Array.<AreaState>}
   */
  split(splitSegment, idStorage) {
    const splitOutput = splitPolygon(splitSegment, this.getFlattenPolygonOfOriginal(true));

    if (splitOutput.length < 2) {
      throw Error('Area split created less then 2 new areas.');
    }

    this.removeAllBreakerBoards();

    const areas = [];

    for (const polygon of splitOutput) {
      areas.push(this._createAreaStateFromPolygon(polygon, idStorage.createId(), this.availableBoards));
    }

    // Create connections between newly created areas
    const newConnections = [];
    const processedAreasIds = [];

    for (const areaStateA of areas) {
      processedAreasIds.push(areaStateA.id);

      for (const areaStateB of areas) {
        if (!processedAreasIds.includes(areaStateB.id)) {
          const connection = this.connectionStorage.createSplitConnections(areaStateA, areaStateB, splitSegment);
          if (connection) {
            newConnections.push(connection);
          }
        }
      }
    }

    if (newConnections.length < areas.length - 1) {
      throw Error(
        `Newly splitted area has created ${areas.length} areas but not at least ${areas.length - 1} new connections.`
      );
    }

    // If the current splitted area already had some connections, transfer them to the children if needed
    for (const connection of this.connectionStorage.getConnections(this)) {
      const virtualSplitSegment = new Segment(
        [...this.points, this.points[0]][connection.getIndexOf(this)],
        [...this.points, this.points[0]][connection.getIndexOf(this) + 1]
      );
      for (const areaState of areas) {
        this.connectionStorage.createSplitConnections(
          connection.getConnectedArea(this),
          areaState,
          virtualSplitSegment
        );
      }
    }

    return areas;
  }

  deleteBorder(connection, idStorage) {
    const polygon = this._getMergedPolygon(connection);
    const newArea = this._createAreaStateFromPolygon(polygon, idStorage.createId(), this.availableBoards);

    // Remove connenction between merged areas
    this.connectionStorage.removeConnectionsBetweenAreas(...connection.getAreas());

    // If the merged areas already had some connections, transfer them to the newly created area
    for (const area of connection.getAreas()) {
      const connections = this.connectionStorage.getConnections(area);
      if (area.cutoutPolygons.length) {
        newArea.cutoutPolygons = area.cutoutPolygons;
      }
      for (const conn of connections) {
        const connectedArea = conn.getConnectedArea(area);
        const segment = new Segment(
          [...connectedArea.originalPoints, connectedArea.originalPoints[0]][conn.getIndexOf(connectedArea)],
          [...connectedArea.originalPoints, connectedArea.originalPoints[0]][conn.getIndexOf(connectedArea) + 1]
        );
        this.connectionStorage.createSplitConnections(newArea, connectedArea, segment);
      }
    }

    // Remove all connections for merged areas
    this.connectionStorage.removeConnectionsOf(connection.items[0].area);
    this.connectionStorage.removeConnectionsOf(connection.items[1].area);

    return newArea;
  }

  removeBreakerBoardOnIndex(index, idStorage) {
    const currentBreakerBoard = this.getBreakerBoardOnIndex(index);
    if (currentBreakerBoard) {
      this._removeBreakerBoardFromStore(index);

      if (this._isInnerBorder(index)) {
        const prevIndex = this._getPrevIndex(index);
        const nextIndex = this._getNextIndex(index);

        // if previous index is inner border and has breaker board, throw it away and replace it
        if (this._isInnerBorder(prevIndex) && this.getBreakerBoardOnIndex(prevIndex)) {
          const plankCount = this.getBreakerBoardOnIndex(prevIndex).plankCount;
          this._removeBreakerBoardFromStore(prevIndex);
          this.placeBreakerBoards(prevIndex, plankCount, idStorage);
        }

        // if next index is inner border and has breaker board, throw it away and replace it
        if (this._isInnerBorder(nextIndex) && this.getBreakerBoardOnIndex(nextIndex)) {
          const plankCount = this.getBreakerBoardOnIndex(nextIndex).plankCount;
          this._removeBreakerBoardFromStore(nextIndex);
          this.placeBreakerBoards(nextIndex, plankCount, idStorage);
        }
      }
      this._recalculatePoints();
    }
  }

  _removeBreakerBoardFromStore(index) {
    const currentBreakerBoard = this.getBreakerBoardOnIndex(index);
    if (currentBreakerBoard) {
      const filtered = this.breakerBoards.filter(item => item.id !== currentBreakerBoard.id);
      this.breakerBoards = filtered;
    }
  }

  removeAllBreakerBoards() {
    this.breakerBoards = [];
    this._recalculatePoints();
  }
  /**
   * Places breaker boards on specified border index of this area.
   * The width of the breaker boards area is calculated automatically by multiplication of currently used board width and plank count.
   * If placing a breaker board on an inner edge of the area, the whole group of inner breaker boards is recalculated.
   * @param  {number} index index of the border of the parent area
   * @param  {number} plankCount count of the planks
   * @param  {IdStorage} idStorage id management storage object
   */
  placeBreakerBoards(index, plankCount, idStorage) {
    this.originalPoints = this.originalPoints || dojo.clone(this.points);

    const polygon = this.getFlattenPolygonOfOriginal(true);
    const edgeIndexes = this._getIndexesForBreakerBoardPlacement(index);
    const plankCounts = edgeIndexes.map(i => {
      return i === index ? plankCount : this.getBreakerBoardOnIndex(i).plankCount;
    });

    const edgesData = [];

    for (const [i, areaIndex] of edgeIndexes.entries()) {
      edgesData.push({
        segment: this.getSegmentOnIndex(areaIndex),
        breakerboardWidth: this.rawArea.data.boardwidth * plankCounts[i],
      });
    }
    const output = addBreakerboardAreaSide(polygon, edgesData);
    this._throwErrorIfBreakerBoardsInvalid(edgesData.length, output);

    for (const index of edgeIndexes) {
      this._removeBreakerBoardFromStore(index);
    }

    const area = simplifyPolygon(output[0][0]);
    const areaCutoutPoints = orderPoints(this.originalPoints, getPolygonPoints(area));

    for (const [i, breakerBoardPolygon] of output[1].entries()) {
      const breakerBoard = breakerBoardPolygon;
      const breakerBoardPoints = dojo.clone(getPolygonPoints(breakerBoard));
      const breakerBoardsObject = new BreakerBoard({
        points: breakerBoardPoints,
        id: idStorage.createId(),
        indexInArea: edgeIndexes[i],
        area: this,
        plankCount: plankCounts[i],
      });

      // When breaker boards for more then one edge were required, add cutout points only to the first one
      if (i === 0) {
        breakerBoardsObject.areaCutoutPoints = areaCutoutPoints;
      }

      breakerBoardsObject.backendObjectFromParent = this.backendObjectForChildren;
      breakerBoardsObject.rawArea.data.active_edge = this._findBreakerBoardBaseBorderIndex(
        breakerBoardPoints,
        edgeIndexes[i]
      );
      this.breakerBoards.push(breakerBoardsObject);
    }

    this._recalculatePoints();
  }

  /**
   * Check the validity of resulted geometrical calculation of breakerboards
   * @param  {number} requiredCount Number of edges for that breakerboards were required
   * @param  {array} output Output of the breakerboards placement geometry algorithm
   */
  _throwErrorIfBreakerBoardsInvalid(requiredCount, output) {
    if (output[0].length !== 1) {
      throw Error('There is not exactly one area created by placing the breakerboards');
    }

    if (output[1].length !== requiredCount) {
      throw Error('Number of requested breakerboards does not match received count.');
    }

    const area = simplifyPolygon(output[0][0]);
    const numberOfAreaVertices = [...area.faces][getMainPolygonFaceIndex(area)].size;
    if (numberOfAreaVertices !== this.originalPoints.length) {
      throw Error('Created cropped area has different number of points then the original area');
    }

    for (const bbPolygon of output[1]) {
      const breakerBoard = simplifyPolygon(bbPolygon);
      const numberOfBreakerBoardVertices = [...breakerBoard.faces][getMainPolygonFaceIndex(breakerBoard)].size;
      if (numberOfBreakerBoardVertices !== 4) {
        throw Error('Created breaker board has not exactly 4 points');
      }
    }
  }

  _findBreakerBoardBaseBorderIndex(breakerPoints, areaIndex) {
    const points = [...breakerPoints, breakerPoints[0]];
    const areaVector = this.getVectorOnIndex(areaIndex);
    let index = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const vector = new Vector(points[i], points[i + 1]);
      const angle = areaVector.angleTo(vector);
      const angleTolerance = 0.0001;
      if (
        isEqualInTolerance(angle, 0, angleTolerance) ||
        isEqualInTolerance(angle, Math.PI, angleTolerance) ||
        isEqualInTolerance(angle, 2 * Math.PI, angleTolerance)
      ) {
        index = i;
        break;
      }
    }
    return index;
  }

  /**
   * Return index within area, which should be used as the base border.
   * It is either the closest parallel edge to the base border or 0
   * @param  {AreaState} area
   */
  _findNewAreaBaseBorderIndex(area) {
    const baseBorderVector = this.getVectorOnIndex(this.baseBorderIndex);
    const baseBorderSegment = this.getSegmentOnIndex(this.baseBorderIndex);

    const points = [...area.originalPoints, area.originalPoints[0]];
    let index;
    let distance;

    for (let i = 0; i < points.length - 1; i++) {
      const vector = new Vector(points[i], points[i + 1]);
      const angle = baseBorderVector.angleTo(vector);
      const angleTolerance = 0.0001;
      if (
        isEqualInTolerance(angle, 0, angleTolerance) ||
        isEqualInTolerance(angle, Math.PI, angleTolerance) ||
        isEqualInTolerance(angle, 2 * Math.PI, angleTolerance)
      ) {
        const dist = points[i].distanceTo(baseBorderSegment)[0];
        if (distance === undefined || dist < distance) {
          index = i;
          distance = dist;
        }
      }
    }

    if (index === undefined) {
      index = 0;
    }

    return index;
  }
  /**
   * Loops through the attached breaker boards and checks their cutout areas.
   * Intersect this cutouts and the result is stored as the actual point of this area.
   */
  _recalculatePoints() {
    this.points = dojo.clone(this.originalPoints);
    let tempPolygon = this.getFlattenPolygonOfOriginal();
    for (const breaker of this.breakerBoards) {
      if (breaker.areaCutoutPoints.length) {
        const cutoutPoints = breaker.areaCutoutPoints;
        const polygon = this._getFlattenPolygonOfPoints(cutoutPoints);
        let intersection = intersect(tempPolygon, polygon);
        // Happens when there are all inner lines with breakerboards
        if (!intersection.faces.size) {
          intersection = polygon;
        }
        let intersectionPoints = getPolygonPoints(intersection);
        intersectionPoints = orderPoints(getPolygonPoints(tempPolygon), intersectionPoints);
        tempPolygon = this._getFlattenPolygonOfPoints(intersectionPoints);
      }
    }

    this.points = getPolygonPoints(simplifyPolygon(tempPolygon));
  }

  getFlattenPolygon(withCutouts = false) {
    const polygon = this._getFlattenPolygonOfPoints(this.points);
    if (withCutouts) {
      this._addCutoutToPolygon(polygon);
    }
    return polygon;
  }

  getFlattenPolygonOfOriginal(withCutouts = false) {
    const polygon = this._getFlattenPolygonOfPoints(this.originalPoints);
    if (withCutouts) {
      this._addCutoutToPolygon(polygon);
    }
    return polygon;
  }

  _addCutoutToPolygon(polygon) {
    if (this.cutoutPolygons.length) {
      for (const points of this.cutoutPolygons) {
        const segments = [];
        const closedPoints = [...points, points[0]];
        for (let i = 0; i < closedPoints.length - 1; i++) {
          segments.push(new Segment(closedPoints[i], closedPoints[i + 1]));
        }
        polygon.addFace(segments);
      }
    }
  }

  _getFlattenPolygonOfPoints(points) {
    const closedPoints = [...points, points[0]];
    const segments = [];
    for (let i = 0; i < closedPoints.length - 1; i++) {
      segments.push(new Segment(closedPoints[i], closedPoints[i + 1]));
    }
    const polygon = new Polygon();
    polygon.addFace(segments);

    return polygon;
  }

  /**
   * Returns JSON objects describing this area state. The JSON is compatible with backend and can be consumed by the recalculation service
   * @param addCutouts include virtual standalone cutout areas in the output
   */
  getBackendObjects(addCutouts = true) {
    const objects = [];
    const currentObject = dojo.clone(this.rawArea.data);
    const connections = this.connectionStorage.getDescriptors(this);

    currentObject.descriptor = {};
    currentObject.descriptor.id = this.id;
    currentObject.descriptor.group_id = this.groupId;
    if (connections.length) {
      currentObject.descriptor.connections = connections;
    }

    currentObject.points = this.points.map(point => {
      return { x: point.x, y: point.y };
    });

    if (this.originalPoints) {
      currentObject.descriptor.original_points = this.originalPoints.map(point => {
        return { x: point.x, y: point.y };
      });
    }

    currentObject.current_plan_index = this.currentPlanIndex;

    currentObject.has_breakerboards = this.breakerBoards.length ? true : false;

    if (addCutouts) {
      for (const points of this.cutoutPolygons) {
        objects.push({ cutout: true, points: points });
      }
    }

    currentObject.geometry_stats = {};
    currentObject.geometry_stats.perimeter_with_breakerboards = Math.round(this.perimeterMMWithBreakerboards);
    // rawArea.stats might not exist before the recalculation is made (e. g. area splitting), use 0 then
    currentObject.geometry_stats.area = this.rawArea.stats ? this.rawArea.stats.surface_mm2 : 0;
    currentObject.geometry_stats.area_with_breakerboards = this.surfaceMM2;
    currentObject.geometry_stats.area_unit = 'mm2';

    objects.push(currentObject);

    // Trims
    const userTrimsSelection = this.borderLabelState.getBackendObjects();
    if (Object.keys(userTrimsSelection).length !== 0) {
      currentObject.user_trims_selection = userTrimsSelection;
    }

    for (const breakerBoard of this.breakerBoards) {
      objects.push(breakerBoard.backendObject);
    }

    return objects;
  }

  updateRawAreaData(key, value) {
    this.rawArea.data[key] = value;
  }

  /**
   * Checks if there is some connection to the area on the specified index
   * @param  {number} index
   */
  hasConnectionOnIndex(index) {
    return !!this.getNumberOfConnectionsOnIndex(index);
  }

  /**
   * Get number of connections on specified border index
   * @param  {number} index
   */
  getNumberOfConnectionsOnIndex(index) {
    return this.connectionStorage.getConnectionsOnIndex(this, index).length;
  }

  /**
   * Checks if there is another "transition" connection to the area via some other sibling areas on the same index
   * @param  {Connection} connection
   * @returns {boolean}
   */
  hasTransitionConnectionToTheSame(connection) {
    const area = connection.getConnectedArea(this);
    const index = connection.getIndexOf(area);

    // all other areas to that this area is connected
    const areas = this.connectionStorage.getAllConnectedAreas(this).filter(a => a.id !== area.id);
    for (const checkedArea of areas) {
      if (this.connectionStorage.hasConnectionBetweenAreas(checkedArea, area)) {
        const conn = this.connectionStorage.getConnectionBetweenAreas(checkedArea, area);
        if (conn.getIndexOf(area) === index) {
          return true;
        }
      }
    }

    return false;
  }

  get currentPlanIndex() {
    return this.rawArea.data.current_plan_index === undefined
      ? this.defaultPlanIndex
      : this.rawArea.data.current_plan_index;
  }

  get defaultPlanIndex() {
    return this.indexedBoardLayoutsSorted.length ? this.indexedBoardLayoutsSorted[0].index : 0;
  }

  set currentPlanIndex(index) {
    this.updateRawAreaData('current_plan_index', index);
  }

  resetCurrentPlanIndex() {
    this.rawArea.data.current_plan_index = this.defaultPlanIndex;
  }

  updateBorderLabelState() {
    this.borderLabelState.setState(this.rawArea);
  }

  clearBorderLabelState() {
    this.borderLabelState.clearState(this.rawArea);
  }

  get currentJoistSpacingPerpendicular() {
    return this.rawArea.data.joist_spacing;
  }

  set currentJoistSpacingPerpendicular(distance) {
    this.updateRawAreaData('joist_spacing', distance);
    this.updateRawAreaData('joist_spacing_perpendicular', distance);
  }

  get currentJoistSpacingParallel() {
    return this.rawArea.data.joist_spacing_parallel;
  }

  set currentJoistSpacingParallel(distance) {
    this.updateRawAreaData('joist_spacing_parallel', distance);
  }

  findBreakerBoardById(id) {
    const breakerBoard = this.breakerBoards.find(el => el.id === id);
    if (!breakerBoard) {
      throw Error('Breaker board with specified id does not exist');
    }

    return breakerBoard;
  }

  getBreakerBoardOnIndex(index) {
    for (const breaker of this.breakerBoards) {
      if (breaker.indexInArea === index) {
        return breaker;
      }
    }
    return false;
  }

  _isInnerBorder(index) {
    const polygon = this.getFlattenPolygonOfOriginal();
    const currentVector = this.getVectorOnIndex(index);
    const prevVector = this.getVectorOnIndex(this._getPrevIndex(index));
    const nextVector = this.getVectorOnIndex(this._getNextIndex(index));

    const isInner = isInnerBorder(
      currentVector,
      prevVector,
      nextVector,
      [...polygon.faces][0].orientation() === ORIENTATION.CW
    );

    return isInner;
  }

  _getPrevIndex(index) {
    return index === 0 ? this.originalPoints.length - 1 : index - 1;
  }

  _getNextIndex(index) {
    return index === this.originalPoints.length - 1 ? 0 : index + 1;
  }

  /**
   * Returns array of indexes as an ipnut for geometrical breaker board algorithm
   * @param  {number} index
   * @returns {Array.<number>}
   */
  _getIndexesForBreakerBoardPlacement(index) {
    let indexes = [];

    if (this._isInnerBorder(index)) {
      indexes.push(index);
      indexes = this._getPrevIndexesForBreakerBoardPlacement(index, indexes);
      indexes = this._getNextIndexesForBreakerBoardPlacement(index, indexes);
    } else {
      indexes.push(index);
    }

    return indexes;
  }

  _getPrevIndexesForBreakerBoardPlacement(index, usedIndexes) {
    let indexes = usedIndexes;
    const prevIndex = this._getPrevIndex(index);
    if (!indexes.includes(prevIndex) && this._isInnerBorder(prevIndex) && this.getBreakerBoardOnIndex(prevIndex)) {
      indexes = [prevIndex, ...indexes];
      indexes = [...this._getPrevIndexesForBreakerBoardPlacement(prevIndex, indexes)];
    }
    return indexes;
  }

  _getNextIndexesForBreakerBoardPlacement(index, usedIndexes) {
    let indexes = usedIndexes;
    const nextIndex = this._getNextIndex(index);
    if (!indexes.includes(nextIndex) && this._isInnerBorder(nextIndex) && this.getBreakerBoardOnIndex(nextIndex)) {
      indexes.push(nextIndex);
      indexes = [...this._getNextIndexesForBreakerBoardPlacement(nextIndex, indexes)];
    }

    return indexes;
  }

  getVectorOnIndex(index) {
    const points = [...this.originalPoints, this.originalPoints[0]];
    return new Vector(points[index], points[index + 1]);
  }

  getSegmentOnIndex(index) {
    const points = [...this.originalPoints, this.originalPoints[0]];
    return new Segment(points[index], points[index + 1]);
  }

  _getMergedPolygon(connection) {
    const areaA = connection.items[0].area;
    const indexA = connection.items[0].index;
    const areaB = connection.items[1].area;
    const indexB = connection.items[1].index;

    const mergedPoints = [];

    for (const [index, point] of areaA.originalPoints.entries()) {
      if (!containsPoint(mergedPoints, point)) {
        mergedPoints.push(point);
      }
      if (index === indexA) {
        for (let i = areaB.getNextIndex(indexB); i <= Math.max(areaB.originalPoints.length - 1, indexB); i++) {
          const newPoint = areaB.originalPoints[i];
          if (!containsPoint(mergedPoints, newPoint)) {
            mergedPoints.push(newPoint);
          }
        }

        for (let i = 0; i <= indexB; i++) {
          const newPoint = areaB.originalPoints[i];
          if (!containsPoint(mergedPoints, newPoint)) {
            mergedPoints.push(newPoint);
          }
        }
      }
    }
    return simplifyPolygon(this._getFlattenPolygonOfPoints(mergedPoints));
  }

  getPrevIndex(index) {
    return index === 0 ? this.originalPoints.length - 1 : index - 1;
  }

  getNextIndex(index) {
    return index === this.originalPoints.length - 1 ? 0 : index + 1;
  }

  /**
   * Checks, if border on specified index can be deleted.
   * The border can be deleted if:
   *  - there is some connection to other area on the index
   *  - there is only one connection to other area on the index
   *  - there is not any sibling area connected to the same area on the same index (avoid creation of irreversible states)
   *
   * @param  {nubmer} index
   */
  canDeleteBorder(index) {
    return (
      this.hasConnectionOnIndex(index) &&
      this.getNumberOfConnectionsOnIndex(index) === 1 &&
      !this.hasTransitionConnectionToTheSame(this.connectionStorage.getConnectionsOnIndex(this, index)[0])
    );
  }

  set rawArea(rawArea) {
    this._rawArea = rawArea;
  }

  get rawArea() {
    return this._rawArea;
  }

  get backendObjectForChildren() {
    const data = dojo.clone(this.rawArea.data);
    data.current_plan_index = 0;
    delete data.descriptor;
    delete data.has_breakerboards;
    delete data.geometry_stats;
    delete data.user_trims_selection;
    return data;
  }

  set backendObjectFromParent(data) {
    this.rawArea = {
      data: data,
    };
    this.rawArea.data.active_edge = 0;
    this.rawArea.data.current_plan_index = 0;
    delete this.rawArea.data.points;
  }

  get baseBorderIndex() {
    return parseInt(this.rawArea.data.active_edge);
  }

  set id(id) {
    this._id = id;
  }

  get id() {
    return this._id;
  }

  get originalRawPoints() {
    if (this.originalPoints) {
      return this.originalPoints.map(point => {
        return { x: point.x, y: point.y };
      });
    } else {
      return this.points.map(point => {
        return { x: point.x, y: point.y };
      });
    }
  }

  get tilesTranslation() {
    return this.rawArea.data.tiles_translation === undefined ? 0 : this.rawArea.data.tiles_translation;
  }

  get currentTileShift() {
    return this.rawArea.data.tile_shift === undefined ? 1 : this.rawArea.data.tile_shift;
  }

  set currentTileShift(tileShift) {
    this.updateRawAreaData('tile_shift', tileShift);
  }

  get usedBoardLengthCounts() {
    let boardCounts = getLengthCountsForArea(this.rawArea.finalPlans.plans[this.currentPlanIndex]);
    for (const breakerBoard of this.breakerBoards) {
      const breakerBoardCounts = breakerBoard.usedBoardLengthCounts;
      boardCounts = mergeTwoLengthCounts(boardCounts, breakerBoardCounts);
    }
    return boardCounts;
  }

  get boardAreaMM2() {
    const boardCounts = this.usedBoardLengthCounts;

    return Object.keys(boardCounts).reduce((acc, key) => {
      return acc + key * boardCounts[key] * cmToMm(this.boardWidthCM);
    }, 0);
  }

  get waste() {
    return this.rawArea.finalPlans.plans[this.currentPlanIndex].stats.norm.waste_linear;
  }

  get surfaceMM2() {
    // rawArea.stats might not exist before the recalculation is made (e. g. area splitting), use 0 then
    let areaMM2 = this.rawArea.stats ? this.rawArea.stats.surface_mm2 : 0;
    for (const breakerBoard of this.breakerBoards) {
      areaMM2 += breakerBoard.surfaceMM2;
    }
    return areaMM2;
  }

  get perimeterMMWithBreakerboards() {
    let perimeter = 0;
    const faces = [...this.getFlattenPolygonOfOriginal(true).faces];
    for (const face of faces) {
      for (const edge of face) {
        perimeter += edge.length;
      }
    }
    return perimeter;
  }

  get boardLayouts() {
    return this.rawArea.finalPlans ? this.rawArea.finalPlans.plans : [];
  }

  get boardWidthCM() {
    return this.rawArea.data.boardwidth ?? this.rawArea.data.tilewidth;
  }

  set selectedBoardLengths(boardLengths) {
    this.updateRawAreaData('boards', boardLengths);
    for (const breakerBoard of this.breakerBoards) {
      breakerBoard.updateRawAreaData('boards', boardLengths);
    }
  }

  get indexedBoardLayoutsSorted() {
    if (this.boardLayouts.length <= 1) return [];
    // skip for legacy compatibility
    if (!this.boardLayouts[0].stats.boards.end_on_end_lines) {
      return this.boardLayouts.map((layout, index) => {
        return { index, layout, price: 0 };
      });
    }
    let deletedLayouts = [];
    const indexedLayouts = this.boardLayouts.map((layout, index) => {
      const usedBoards = getLengthCountsForArea(this.rawArea.finalPlans.plans[index]);
      let price = 0;
      for (const board of this.availableBoards) {
        if (board.price && board.length in usedBoards) {
          price += board.price * usedBoards[board.length];
        }
      }
      return { index, layout, price };
    });

    let noDuplicateLayouts = indexedLayouts.reduce((acc, { layout, index, price }) => {
      const duplicateIndex = acc.findIndex(({ layout: layoutAcc }) => this._areEqualPatternLayouts(layout, layoutAcc));
      if (duplicateIndex === -1) {
        return [...acc, { layout, index, price }];
      }
      const existingLayout = acc[duplicateIndex];
      if (
        existingLayout.layout.stats.optimization &&
        ((existingLayout.layout.stats.optimization.id === 'boardsRatio' &&
          existingLayout.price > (1 + this.parallelLayoutOptions.weightedSolutionsBias) * price) ||
          (existingLayout.layout.stats.optimization.id !== 'boardsRatio' &&
            existingLayout.price * (1 + this.parallelLayoutOptions.weightedSolutionsBias) >= price))
      ) {
        deletedLayouts.push(existingLayout);
        acc[duplicateIndex] = { layout, index, price };
      } else {
        deletedLayouts.push({ layout, index, price });
      }
      return acc;
    }, []);

    const planMinCuts = noDuplicateLayouts.reduce((prev, curr) => {
      return prev.layout.stats.boards.end_on_end_joins < curr.layout.stats.boards.end_on_end_joins ? prev : curr;
    });

    // could be undefined for old plans not containing this info
    const minDoubleJoists = noDuplicateLayouts.reduce((currentMin, layout) => {
      return layout.layout.stats.boards.end_on_end_lines.length < currentMin
        ? layout.layout.stats.boards.end_on_end_lines.length
        : currentMin;
    }, Number.MAX_SAFE_INTEGER);

    noDuplicateLayouts = noDuplicateLayouts
      .filter(layout => layout.index !== planMinCuts.index)
      .sort((layoutA, layoutB) => layoutA.layout.stats.norm.waste_linear - layoutB.layout.stats.norm.waste_linear);

    const maxDoubleJoists = minDoubleJoists + this.parallelLayoutOptions.maxBoardEndLinesDelta;
    if (!isNaN(maxDoubleJoists)) {
      noDuplicateLayouts.sort(
        (layoutA, layoutB) =>
          layoutA.layout.stats.boards.end_on_end_lines.length - layoutB.layout.stats.boards.end_on_end_lines.length
      );
      const symmetricalLayouts = [];
      const nonSymmetricalLayouts = [];
      noDuplicateLayouts.forEach(({ layout, index, price }) => {
        if (this._isUniformDistributionLayout(layout)) {
          if (this.debug) layout.stats.debug1 = 'symmetrical';
          symmetricalLayouts.push({ layout, index, price });
        } else {
          nonSymmetricalLayouts.push({ layout, index, price });
        }
      });
      deletedLayouts = deletedLayouts.map(({ layout, index, price }) => {
        if (this._isUniformDistributionLayout(layout)) {
          if (this.debug) layout.stats.debug1 = 'symmetrical';
        }
        return { layout, index, price };
      });
      noDuplicateLayouts = [...symmetricalLayouts, ...nonSymmetricalLayouts];
      noDuplicateLayouts = noDuplicateLayouts.reduce((acc, { layout, index, price }) => {
        if (layout.stats.boards.end_on_end_lines.length <= maxDoubleJoists) {
          return [...acc, { layout, index, price }];
        } else {
          deletedLayouts.push({ layout, index, price });
          return acc;
        }
      }, []);

      if (noDuplicateLayouts.length > this.parallelLayoutOptions.maxNumProposals - 1) {
        deletedLayouts.push(...noDuplicateLayouts.slice(this.parallelLayoutOptions.maxNumProposals - 1));
        noDuplicateLayouts = noDuplicateLayouts.slice(0, this.parallelLayoutOptions.maxNumProposals - 1);
      }
    }

    if (this.debug)
      console.log(
        'original:',
        this.boardLayouts.length,
        'kept',
        noDuplicateLayouts.length + 1,
        'deleted',
        deletedLayouts.length
      );
    // return layout with min cuts first
    const returnedLayouts = [planMinCuts, ...noDuplicateLayouts];
    if (this.debug)
      returnedLayouts.push(
        ...deletedLayouts.map(indexLayout => {
          indexLayout.layout.stats.debug = 'to_be_deleted';
          return indexLayout;
        })
      );
    return returnedLayouts;
  }

  _areEqualPatternLayouts(layoutA, layoutB) {
    if (
      layoutA.stats.optimization &&
      layoutA.stats.optimization.parallelStaggeredPattern &&
      layoutB.stats.optimization &&
      layoutB.stats.optimization.parallelStaggeredPattern &&
      ((layoutA.stats.optimization.parallelStaggeredPattern.even ===
        layoutB.stats.optimization.parallelStaggeredPattern.even &&
        layoutA.stats.optimization.parallelStaggeredPattern.odd ===
          layoutB.stats.optimization.parallelStaggeredPattern.odd) ||
        (layoutA.stats.optimization.parallelStaggeredPattern.even ===
          layoutB.stats.optimization.parallelStaggeredPattern.odd &&
          layoutA.stats.optimization.parallelStaggeredPattern.odd ===
            layoutB.stats.optimization.parallelStaggeredPattern.even))
    ) {
      return true;
    }
    return false;
  }

  _isSymmetricalLayout(layout) {
    if (layout.stats.optimization && layout.stats.optimization.parallelStaggeredPattern) {
      const binaryEven = (
        layout.stats.optimization.parallelStaggeredPattern.even | layout.stats.optimization.parallelStaggeredPattern.odd
      ).toString(2);
      //const binaryOdd = layout.stats.optimization.parallelStaggeredPattern.odd.toString(2);
      const reversedBinaryEven = binaryEven.split('').reverse().join('');
      //const reversedBinaryOdd = binaryOdd.split("").reverse().join("");
      if (binaryEven === reversedBinaryEven) {
        return true;
      }
    }
    return false;
  }

  _isUniformDistributionLayout(layout) {
    if (layout.stats.optimization && layout.stats.optimization.parallelStaggeredPattern) {
      const binaryArray = (
        layout.stats.optimization.parallelStaggeredPattern.even | layout.stats.optimization.parallelStaggeredPattern.odd
      )
        .toString(2)
        .split('')
        .map(char => parseInt(char));
      const secondSupportIndex = binaryArray.findIndex(support => support === 1);
      let prevBoardNumSupports = 0;
      let boardNumSupports = 0;
      for (const supportJoist of binaryArray.slice(secondSupportIndex + 1)) {
        if (supportJoist === 1) {
          if (prevBoardNumSupports === 0) {
            prevBoardNumSupports = boardNumSupports;
          } else if (prevBoardNumSupports !== boardNumSupports) {
            return false;
          }
          boardNumSupports = 0;
        } else {
          boardNumSupports++;
        }
      }
      return true;
    }
  }
}
